import type { Thing } from 'schema-dts'
import type { Brand, AssetType, Environment } from '@xcel-next/core/enums'
import type { SitemapType } from '@xcel-next/types'

type EnvironmentAsKeyWithStringValue = { [key in Environment]?: string }

export enum Salutations {
  Informal = 'informal',
  Formal = 'formal'
}

export type brandSalutation = Salutations

export type headScriptObject = {
  src: string,
  async: boolean,
  vmid?: string
}

type HtmlHeadMeta = {
  name?: string,
  charset?: string,
  content?: string,
  'data-viewport'?: string,
  'http-equiv'?: string,
  vmid?: string
}

type HtmlHeadLink = {
  rel: string,
  href: string,
  as?: string,
  type?: string,
  crossOrigin?: string,
  sizes?: string,
  vmid?: string
}

export enum AdaSlot {
  Superbanner = 'superbanner_1',
  Wallpaper = 'wallpaper_1',
  Skyscraper1 = 'skyscraper_1',
  Skyscraper2 = 'skyscraper_2',
  Rectangle1 = 'rectangle_1',
  Rectangle2 = 'rectangle_2',
  Mobile1 = 'mobile_1',
  Mobile2 = 'mobile_2',
  Mobile3 = 'mobile_3',
  Mobile10 = 'mobile_10'
}

export enum SocialProvider {
  Facebook = 'facebook',
  Instagram = 'instagram',
  Pinterest = 'pinterest',
  Twitter = 'twitter',
  YouTube = 'youtube',
  WhatsApp = 'whatsapp',
  Xing = 'xing',
  MailTo = 'mailto',
  LinkedIn = 'linkedin',
  TikTok = 'tiktok',
  Test = 'test',
  Spiele = 'spiele',
  Win = 'win',
  Flipboad = 'flipboard',
  Spotify = 'spotify',
  Reddit = 'reddit',
  Print = 'print',
  Newsletter = 'mail'
}

type EmbedBase = {
  scriptUrl?: string
}

type EmbedFacebook = EmbedBase & {
  testAppId?: string,
  version: string,
  autoLogAppEvents: boolean
}

type FacebookApp = EmbedFacebook & {
  appId: string,
  scope?: string
}

type AgfConfig = {
  enabled?: boolean,
  vcid?: string,
  appId: {
    static?: string,
    streaming?: string
  },
  segAPrefix?: string
}

type Consent = {
  embedCookieName: 'euconsent-v2-embeds',
  idToVendorMap: Record<string, string>,
  vendor: Record<string, string>,
  // Sourcepoint custom purpose id that gates the social embeds ("Drittanbieter-Inhalte ...").
  // Differs per Sourcepoint account, hence per environment (testing/staging/dev share one account).
  thirdPartyContentPurposeId: EnvironmentAsKeyWithStringValue
}

export enum LinkTarget {
  Blank = '_blank',
  Self = '_self',
  Parent = '_parent',
  Top = '_top'
}

export enum TaboolaPageType {
  Article = 'article',
  Category = 'category'
}

type TaboolaPageContext =
  | { [TaboolaPageType.Article]: 'auto' }
  | { [TaboolaPageType.Category]: 'auto' }

export enum TaboolaSlotConfigTargetType {
  Mix = 'mix' // extend this if need be
}

export type TaboolaSlotConfig = {
  mode: string,
  container: string,
  placement: string,
  'target_type': TaboolaSlotConfigTargetType
} | false

type AuthorHeaderDisplayDateFormat = 'DD.MM.YYYY, HH:mm' | 'DD.MM.YYYY'

export enum ImageSrcSetFormat {
  Landscape = 'landscape',
  Square = 'square',
  Portrait = 'portrait',
  Rectangle = 'rectangle'
}

type ImageSrcSet = {
  viewportWidth: string,
  width: string,
  format?: ImageSrcSetFormat
}

type ImageSrcSetDefinition = {
  maxWidth?: number,
  srcSet?: ImageSrcSet[],
  individualScaleFactor?: number
}

export type SliderOptions = {
  initialIndex?: number,
  contain?: boolean,
  cellAlign?: string,
  lazyLoad?: number,
  pageDots?: boolean,
  wrapAround?: boolean,
  freeScroll?: boolean,
  prevNextButtons?: boolean,
  arrowShape?: string,
  autoPlay?: boolean,
  fullScreen?: boolean,
  adaptiveHeight?: boolean,
  dragThreshold?: number,
  selectedAttraction?: number,
  friction?: number,
  freeScrollFriction?: number,
  accessibility?: boolean,
  percentPosition?: boolean,
  groupCells?: boolean,
  resize?: boolean,
  setGallerySize?: boolean,
  imagesLoaded?: boolean,
  cellSelector?: string
}

type SliderImageConfig = {
  width: number,
  height: number,
  placeholder: string
}

export type SliderItem = {
  name: string,
  url: string,
  src?: string
}

export enum SliderType {
  Default = 'bx-teaser-container--slider',
  Single = 'bx-teaser-container--slider-single',
  VodEpg = 'bx-teaser-container--vod-epg-slider'
}

type TeaserGroupSlider = {
  [key in SliderType]?: {
    teaserGroupType: string,
    customOptions: SliderOptions
  }
}

type Slider = {
  nextPrevArrowPath?: string,
  triggerGAOnSlide?: boolean,
  triggerAGFOnAdReload?: boolean,
  forceTeaserGroup?: {
    type: string,
    buttonPositionLogic: string
  },
  enableOverview?: boolean,
  teaserGroupSlider?: TeaserGroupSlider,
  starsignSlider?: {
    imageConfig?: SliderImageConfig,
    items: SliderItem[]
  }
}

export type Taboola = {
  enabled: boolean,
  isDefault?: boolean,
  script?: string,
  scriptPrivacy?: string,
  pixelUrl?: string,
  pageContext?: TaboolaPageContext,
  urls?: string[], // list of urls where the taboola script should be loaded; could be exact matches, e.g. "/this-is-an-article-url-12345" or prefix wildcards, e.g. "/subfolder/*"
  beforeSeoTextPageIds?: string[], // layout IDs where the existing feed moves directly before the SEO text, if present
  slotConfigs?: {
    slot1?: TaboolaSlotConfig,
    slot2?: TaboolaSlotConfig
  }
}

type StructuredDataOverride = {
  urlPattern: RegExp,
  data: {
    '@context': string,
    '@graph': Thing[]
  }
}

type StructuredDataOverrides = StructuredDataOverride[];

type ReloadAdsOnInteraction = {
  enabled: boolean,
  config: AdSlotReloadConfig
}

export type AdSlotReloadConfig = {
  reloadAdaSlots?: string[],
  minDisplayDuration: number,
  clickCount: number
}

type OrderedSlotLists = {
  article?: AdDimensions,
  structureterm?: AdDimensions,
  asyncContent?: AdDimensions,
  videoOnlyArticle?: AdDimensions
}

type AdDimensions = {
  mobile?: Array<string>,
  smallDesktop?: Array<string>,
  largeDesktop?: Array<string>
}

export type DynamicAdSlots = {
  enabled: boolean,
  stickyChunkHeight?: number,
  asideAdSlots?: Array<string>,
  slotDistanceOffset?: AdSlotUsageDimensions,
  startOffsetTop?: AdSlotUsageDimensions,
  startOffsetTopArticle?: AdSlotArticleUsageDimensions,
  orderedSlotLists?: OrderedSlotLists
}

type AdSlotUsageDimensions = {
  mobile?: (DynamicAdClientConfig: DynamicAdClientConfig) => number,
  smallDesktop?: (DynamicAdClientConfig: DynamicAdClientConfig) => number,
  largeDesktop?: (DynamicAdClientConfig: DynamicAdClientConfig) => number
}

export type DynamicAdArticleClientConfig = Omit<DynamicAdClientConfig, 'article'> & {
  article: {
    asideWrapperHeight: number,
    leadOffset: number
  }
}

type AdSlotArticleUsageDimensions = {
  mobile?: (config: DynamicAdArticleClientConfig) => number,
  smallDesktop?: (config: DynamicAdArticleClientConfig) => number,
  largeDesktop?: (config: DynamicAdArticleClientConfig) => number
}

type AdSlotUsage = {
  moltenBundlePreviewBrandPath?: string,
  adDefendBackfill?: boolean,
  dynamicAdSlots?: DynamicAdSlots,
  taboola?: Taboola[],
  seeding?: boolean,
  desktopAdsOnTablets?: boolean,
  reloadAdsOnInteraction?: ReloadAdsOnInteraction
}

export type DynamicAdClientConfig = {
  vw: number,
  vh: number,
  screenType?: string,
  startOffsetTop?: number,
  slotDistanceOffset?: number,
  article?: {
    asideWrapperHeight?: number,
    leadOffset?: number
  },
  slotList?: Array<string>,
  exclusionList?: Array<string>
}

type ImageLazyLoading = {
  aboveTheFoldParagraphCount: {
    [AssetType.Article]: number,
    [AssetType.StructureTerm]: number
  }
}

type AgeDeXml = {
  ageIssuer: string,
  lastChange: string,
  revisitAfter: string,
  age: string,
  minAge: string,
  defaultAge: string,
  scope: string
}

type SocialShare = {
  article?: SocialProvider[],
  horoscope?: SocialProvider[],
  enableTopPosition?: boolean
}

type SocialSettings = {
  shareBaseUrl?: EnvironmentAsKeyWithStringValue,
  socialLinks?: {
    [value in SocialProvider]?: string
  },
  socialShare?: SocialShare,
  mailto?: {
    subject?: string,
    body?: string
  },
  socialLoginEnabled?: {
    [key in Environment]?: {
      [value in SocialProvider]?: boolean
    }
  },
  apps?: {
    [key in Environment]?: {
      [SocialProvider.Facebook]: FacebookApp
    }
  }
}

type Embeds = {
  [SocialProvider.Facebook]: EmbedFacebook,
  [SocialProvider.Twitter]: EmbedBase,
  [SocialProvider.Instagram]: EmbedBase,
  [SocialProvider.YouTube]: EmbedBase,
  [SocialProvider.Pinterest]: EmbedBase
}

type JotForm = {
  account: 'default' | Brand.happinez | 'houseofstars',
  humanReadableName: 'XCEL' | Brand.happinez | 'House of Stars',
  baseUrl: 'https://bauerxcelmedia.jotform.com' | 'https://form.jotform.com' | 'https://bauermedia.jotform.com'
}

type Carwow = {
  baseUrl?: string,
  cta?: { // call-to-action
    affiliate?: string, // e.g. "azo"
    utmCampaign?: string // e.g. "af_de_bauer-azo"
  }
}

export type DefaultBrandConfig = {
  brandSalutation?: Salutations,
  actualBrandName?: string,
  googlePreferredSourceDomain?: string,
  htmlHeadScriptSources?: {
    [key in Environment]: headScriptObject[]
  },
  htmlHeadMetas: HtmlHeadMeta[],
  htmlHeadLinks: HtmlHeadLink[],
  availableBrandPageStyles?: Record<string, string>,
  isSeoHeadlineEnabled?: boolean,
  cdnUri?: EnvironmentAsKeyWithStringValue,
  cdnIconSpritePaths?: string[],
  imageServiceUrl: EnvironmentAsKeyWithStringValue,
  stripePublicKey?: EnvironmentAsKeyWithStringValue,
  paypalClientId?: EnvironmentAsKeyWithStringValue,
  proxyServiceUrl?: EnvironmentAsKeyWithStringValue,
  orchestrationServiceUrl?: EnvironmentAsKeyWithStringValue,
  searchServiceUrl?: EnvironmentAsKeyWithStringValue,
  vodServiceUrl?: EnvironmentAsKeyWithStringValue,
  epgSearchServiceUrl?: EnvironmentAsKeyWithStringValue,
  epgAutocompleteSearchServiceUrl?: EnvironmentAsKeyWithStringValue,
  hotOrNotServiceUrl?: EnvironmentAsKeyWithStringValue,
  fileServiceUrl?: EnvironmentAsKeyWithStringValue,
  ascendantCalculatorServiceUrl?: EnvironmentAsKeyWithStringValue,
  ascendantPagesBaseUrl?: EnvironmentAsKeyWithStringValue,
  trackingServiceUrl?: {
    [key in Environment]: '/rec'
  },
  userArea?: {
    redirectToAfterLogin?: string,
    enableRecipeRating: boolean
  },
  agfConfig?: AgfConfig,
  consent?: Consent,
  googleTagManagerId?: string,
  googleAnalytics4?: {
    measurementId?: string,
    endpointHostname: string
  },
  googleMapsApi?: {
    key: string,
    url: string
  },
  structuredDataOverrides?: StructuredDataOverrides | [],
  adSlotUsage?: AdSlotUsage,
  authorHeaderDisplayDateFormat?: AuthorHeaderDisplayDateFormat,
  imageSrcDefinitions?: {
    isEnabled: boolean,
    maxWidth?: number,
    definitions: Record<string, ImageSrcSetDefinition>
  },
  imageLazyLoading?: ImageLazyLoading,
  slider?: Slider,
  videoPlayer?: {
    relatedOverlayModeEnabled?: boolean // desktop - mobile is enabled globally for all brands
  },
  ageDeXml?: AgeDeXml,
  sitemapTypes?: SitemapType[],
  socialSettings: SocialSettings,
  embeds?: Embeds,
  jotForm?: JotForm[],
  carwow?: Carwow,
  vgWort?: {
    enabled: boolean,
    url: string
  },
  dynamicComponents?: {
    teaser: boolean
  },
  productLinkTarget?: LinkTarget,
  tagListExcludeTags?: string[],
  sweepstakesActive?: boolean,
  teaserSettings?: {
    shouldShowDownloadIconOnTeaser?: boolean,
    enableInlineTeaserSliderStyleForGalleries?: boolean
  },
  vWForNaviSwitch?: number,
  appendBrandToMetaTitle?: boolean,
  epg?: {
    channelPackages: { [key: string]: string }[],
    broadcastCategories: { [key: number]: string }[]
  },
  disallowedQueryParameters?: string[]
}

export type BrandConfigs = Partial<Record<Brand, DefaultBrandConfig>>

export type RegroupedBrandConfig = {
  [K in keyof DefaultBrandConfig]?: {
    [B in Brand]?: DefaultBrandConfig[K]
  }
}
